import { z } from 'zod';
import { OriginSchema } from './origin';
import { objectIdSchema } from './objectId';
import { TimeSchema } from './time';
import { VenueConfigurationSchema, VenueTemplateSchema } from './venueTemplate';
import { UserSchema } from './user';
import { WithIdSchema } from './utils';
import { PropertySchema } from './property';
import { BrandGroupTypeSchema } from './brand';
import { BufferTimeSchema } from './bufferTime';

export const VenueResourceRequestSchema = z.object({
  _id: objectIdSchema,
  amount: z.number(),
  resourceType: z.string(),
  resourceId: objectIdSchema,
  price: z.number(),
  description: z.string().default(''),
});

export const VenueResourceRequestLegacySchema = z.object({
  _id: objectIdSchema,
  amount: z.number(),
  name: z.string(),
});

export const VenueRequestStatus = [
  'pending',
  'approved',
  'modified',
  'rejected',
  'cancelled',
  'draft',
] as const;

export const PaymentStatus = ['partially-collected', 'pending', 'full', ''] as const;

export const VenueRequestStatusesSchema = z.array(z.enum(VenueRequestStatus));

export const VenueRequestUserSchema = z.object({
  _id: objectIdSchema,
  firstName: z.string(),
  lastName: z.string(),
  email: z.string(),
});

export const VenueRequestCommentSchema = z.object({
  _id: objectIdSchema,
  status: z.enum(VenueRequestStatus).optional(),
  message: z.string(),
  date: z.date(),
  user: VenueRequestUserSchema,
});

export const GroupPartyCommentSchema = VenueRequestCommentSchema.omit({ status: true });

export const VenueRequestFileSchema = z.object({
  _id: objectIdSchema,
  name: z.string(),
  location: z.string(),
});

export const NewGroupPartySchema = z.object({
  origin: OriginSchema.optional(),
  name: z.string(),
  contact: z.string(),
  phone: z.string(),
  email: z.string(),
  notes: z.string(),
  groupId: z.union([objectIdSchema, z.string()]).optional(),
  bookingNo: z.string().default(''),
  groupGuideName: z.string().optional(),
  groupGuideCabin: z.string().optional(),
  size: z.number().int().optional(), // number of guests in party
  groupType: BrandGroupTypeSchema.optional(),
  hospitalityDesk: z.boolean().optional(),
  cabinDrops: z.string().optional(),
  diningPreference: z.string().optional(),
  guestExperience: z.string().optional(),
  prepaidPackages: z.string().optional(),
  embark: z.string().optional(),
  debark: z.string().optional(),
  files: z.array(VenueRequestFileSchema).default([]).optional(),
  internalNotes: z.string(),
  salesEmail: z.string(),
  salesPhone: z.string(),
  paymentStatus: z.enum(PaymentStatus),
  balanceDue: z.string(),
  agencyContactName: z.string().default(''),
  comments: z.array(GroupPartyCommentSchema).default([]),
});

export const GroupPartySchema = NewGroupPartySchema.and(WithIdSchema);

export const BareVenueRequestSchema = z.object({
  _id: objectIdSchema,
  venue: objectIdSchema,
  name: z.string(),
  numGuests: z.number(),
  startTime: TimeSchema,
  duration: z.number(),
  date: z.date(),
  dayIndex: z.number(),
  status: z.enum(VenueRequestStatus),
  // uses resource library
  groupResourceItems: VenueResourceRequestSchema.array().default([]),
  // legacy
  resources: VenueResourceRequestLegacySchema.array().default([]),
  useLegacyResources: z.boolean().default(true),
  packages: z.string().array(),
  dining: z.string().optional(),
  comments: VenueRequestCommentSchema.array(),
  notes: z.string(),
  files: VenueRequestFileSchema.array().optional().nullable(),
  createdBy: objectIdSchema,
  createdAt: z.coerce.date().optional(),
  venueConfig: objectIdSchema.optional(),
  subgroup: objectIdSchema.optional(),
  internalNotes: z.string(),
  bufferTime: BufferTimeSchema,
});

export const GroupSubgroupSchema = z.object({
  _id: objectIdSchema,
  label: z.string(),
  name: z.string(),
  groupGuideName: z.string(),
  groupGuideCabin: z.string(),
  attendees: z.number(),
  files: VenueRequestFileSchema.array(),
  notes: z.string(),
});

export const VenueRequestSchema = BareVenueRequestSchema.omit({
  venue: true,
  createdBy: true,
  subgroup: true,
  venueConfig: true,
}).and(
  z.object({
    venue: VenueTemplateSchema,
    createdBy: UserSchema,
    subgroup: GroupSubgroupSchema.optional(),
    venueConfig: VenueConfigurationSchema.optional(),
  })
);

export const NewGroupBookingSchema = z.object({
  _id: objectIdSchema,
  property: objectIdSchema,
  date: z.date(),
  party: NewGroupPartySchema,
  salesOffice: z.string().optional(),
  requests: BareVenueRequestSchema.array(),
  subgroups: GroupSubgroupSchema.array().optional(),
  createdBy: objectIdSchema,
});

export const BareGroupBookingSchema = NewGroupBookingSchema.extend({
  archived: z.boolean(),
}).extend(WithIdSchema.shape);

export const GroupBookingSchema = BareGroupBookingSchema.extend({
  property: PropertySchema,
  createdBy: UserSchema,
  requests: VenueRequestSchema.array(),
});

export const BareSimpleGroupBookingSchema = GroupBookingSchema.omit({
  requests: true,
  subgroups: true,
})
  .extend({
    createdBy: z.string(),
    archived: z.boolean(),
  })
  .extend(WithIdSchema.shape);

export const GroupReportRequestSchema = z.object({
  voyage: z.string(),
  bookingId: z.string(),
  requestId: z.string(),
});

export type GroupBooking = z.infer<typeof NewGroupBookingSchema>;
export type GroupReportRequest = z.infer<typeof GroupReportRequestSchema>;
export type BareVenueRequest = z.infer<typeof BareVenueRequestSchema>;
export type VenueResourceRequest = z.infer<typeof VenueResourceRequestSchema>;
export type GroupParty = z.infer<typeof GroupPartySchema>;
